"use client"

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  AlertCircle,
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronRight,
  Info,
  Loader2,
  RotateCcw,
  ShieldCheck,
  Sparkles,
  X,
} from "lucide-react"

import { Button } from "@/components/ui/button"
import { FindingFields } from "@/components/review/FindingFields"
import { cn } from "@/lib/utils"

type Severity = "INFO" | "MINOR" | "MAJOR"
type FindingStatus = "PENDING" | "ACCEPTED" | "DISMISSED"
type RunStatus = "QUEUED" | "RUNNING" | "SUCCEEDED" | "FAILED"

type ReviewFinding = {
  id: string
  severity: Severity
  title: string
  detail: string
  sourceQuote: string | null
  evidenceQuote: string | null
  /** Structured submission-field refs (newer findings); older ones fall back to evidenceQuote. */
  fieldRefs?: unknown
  status: FindingStatus
  item: { id: string; title: string } | null
  reviewedBy: { id: string; name: string | null } | null
}

export type ReviewRun = {
  id: string
  status: RunStatus
  summary: string | null
  errorMessage: string | null
  createdAt: string
  framework: { id: string; name: string }
  findings: ReviewFinding[]
}

const SEVERITY_META: Record<Severity, { label: string; icon: React.ElementType; className: string }> = {
  MAJOR: { label: "Major", icon: AlertTriangle, className: "text-destructive" },
  MINOR: { label: "Minor", icon: AlertCircle, className: "text-amber-600 dark:text-amber-500" },
  INFO: { label: "Info", icon: Info, className: "text-muted-foreground" },
}

const POLL_MS = 4000

/**
 * Review results for one generated report: a status badge per framework run,
 * the plain-English summary, and the findings with accept/dismiss controls.
 * Self-contained — drop next to any report row and pass the output's id, or
 * its R2 key when the caller only has the rendered artifact's key.
 */
export function ReportReviewPanel({
  reportOutputId,
  r2Key,
}: {
  reportOutputId?: string
  r2Key?: string
}) {
  const [runs, setRuns] = useState<ReviewRun[] | null>(null)
  const [expanded, setExpanded] = useState(false)
  const [starting, setStarting] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const query = reportOutputId
    ? `reportOutputId=${encodeURIComponent(reportOutputId)}`
    : r2Key
      ? `r2Key=${encodeURIComponent(r2Key)}`
      : null

  const fetchRuns = useCallback(async () => {
    if (!query) return
    try {
      const res = await fetch(`/api/report-review?${query}`)
      if (res.ok) setRuns(await res.json())
    } catch {}
  }, [query])

  useEffect(() => {
    fetchRuns()
  }, [fetchRuns])

  // Keep polling while any run is still in flight.
  const hasActive = useMemo(
    () => (runs ?? []).some((r) => r.status === "QUEUED" || r.status === "RUNNING"),
    [runs]
  )
  useEffect(() => {
    if (!hasActive) {
      if (pollRef.current) clearInterval(pollRef.current)
      pollRef.current = null
      return
    }
    pollRef.current = setInterval(fetchRuns, POLL_MS)
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
      pollRef.current = null
    }
  }, [hasActive, fetchRuns])

  // Latest run per framework is the current verdict; older runs are history.
  const latestRuns = useMemo(() => {
    const byFramework = new Map<string, ReviewRun>()
    for (const run of runs ?? []) {
      if (!byFramework.has(run.framework.id)) byFramework.set(run.framework.id, run)
    }
    return [...byFramework.values()]
  }, [runs])

  const startReview = async () => {
    if (starting) return
    setStarting(true)
    setActionError(null)
    try {
      const res = await fetch("/api/report-review", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(reportOutputId ? { reportOutputId } : { r2Key }),
      })
      if (res.ok) {
        setExpanded(true)
        await fetchRuns()
      } else {
        const data = await res.json().catch(() => null)
        setActionError(data?.error || "Could not start the review.")
      }
    } catch {
      setActionError("Could not start the review.")
    } finally {
      setStarting(false)
    }
  }

  const setFindingStatus = async (findingId: string, status: FindingStatus) => {
    setActionError(null)
    // Optimistic flip; refetch settles the authoritative state.
    setRuns((prev) =>
      (prev ?? []).map((run) => ({
        ...run,
        findings: run.findings.map((f) => (f.id === findingId ? { ...f, status } : f)),
      }))
    )
    try {
      const res = await fetch(`/api/report-review/findings/${findingId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      })
      if (!res.ok) {
        setActionError("Could not update the finding.")
      }
    } catch {
      setActionError("Could not update the finding.")
    } finally {
      fetchRuns()
    }
  }

  if (runs === null) return null

  if (latestRuns.length === 0) {
    return (
      <div className="mt-1">
        <Button variant="ghost" size="sm" onClick={startReview} disabled={starting}>
          {starting ? (
            <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" aria-hidden="true" />
          ) : (
            <Sparkles className="mr-2 h-3.5 w-3.5" aria-hidden="true" />
          )}
          Run AI review
        </Button>
        {actionError && (
          <p role="alert" className="mt-1 text-xs font-medium text-destructive">
            {actionError}
          </p>
        )}
      </div>
    )
  }

  return (
    <div className="mt-2 rounded-lg border border-border/70 bg-muted/20">
      <button
        type="button"
        className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm hover:bg-muted/40"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
      >
        {expanded ? (
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        )}
        <span className="font-medium text-foreground">AI review</span>
        <span className="flex flex-wrap items-center gap-2">
          {latestRuns.map((run) => (
            <RunBadge key={run.id} run={run} />
          ))}
        </span>
      </button>

      {expanded && (
        <div className="flex flex-col gap-4 border-t border-border/60 px-3 py-3">
          {latestRuns.map((run) => (
            <ReviewRunDetail key={run.id} run={run} onFindingStatus={setFindingStatus} />
          ))}
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={startReview} disabled={starting || hasActive}>
              {starting ? (
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" aria-hidden="true" />
              ) : (
                <RotateCcw className="mr-2 h-3.5 w-3.5" aria-hidden="true" />
              )}
              Re-run review
            </Button>
            {actionError && (
              <p role="alert" className="text-xs font-medium text-destructive">
                {actionError}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Purely presentational verdict chip for list surfaces that already hold the
 * aggregate (see lib/review/verdict.ts) — no fetching.
 */
export function ReviewVerdictChip({
  verdict,
}: {
  verdict: {
    active: boolean
    failed: boolean
    clean: boolean
    major: number
    minor: number
    info: number
  } | null
}) {
  if (!verdict) return <span className="text-xs text-muted-foreground">—</span>
  if (verdict.active) {
    return (
      <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
        Reviewing…
      </span>
    )
  }
  if (verdict.failed) {
    return (
      <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-medium text-destructive">
        <X className="h-3 w-3" aria-hidden="true" />
        Failed
      </span>
    )
  }
  if (verdict.clean) {
    return (
      <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-medium text-foreground">
        <ShieldCheck className="h-3 w-3" aria-hidden="true" />
        Clean
      </span>
    )
  }
  const parts = [
    verdict.major > 0 ? `${verdict.major} major` : null,
    verdict.minor > 0 ? `${verdict.minor} minor` : null,
    verdict.info > 0 ? `${verdict.info} info` : null,
  ].filter(Boolean)
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-medium",
        verdict.major > 0 ? "text-destructive" : "text-foreground"
      )}
    >
      {verdict.major > 0 ? (
        <AlertTriangle className="h-3 w-3" aria-hidden="true" />
      ) : (
        <AlertCircle className="h-3 w-3" aria-hidden="true" />
      )}
      {parts.join(", ")}
    </span>
  )
}

/**
 * Read-only verdict badges for one output — the mobile-friendly slice of the
 * panel. Renders nothing while loading or when the output has no runs.
 */
export function ReportReviewBadge({ reportOutputId }: { reportOutputId: string }) {
  const [runs, setRuns] = useState<ReviewRun[] | null>(null)

  useEffect(() => {
    let cancelled = false
    fetch(`/api/report-review?reportOutputId=${encodeURIComponent(reportOutputId)}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled && Array.isArray(data)) setRuns(data)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [reportOutputId])

  const latest = useMemo(() => {
    const byFramework = new Map<string, ReviewRun>()
    for (const run of runs ?? []) {
      if (!byFramework.has(run.framework.id)) byFramework.set(run.framework.id, run)
    }
    return [...byFramework.values()]
  }, [runs])

  if (latest.length === 0) return null
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      {latest.map((run) => (
        <RunBadge key={run.id} run={run} />
      ))}
    </span>
  )
}

function RunBadge({ run }: { run: ReviewRun }) {
  if (run.status === "QUEUED" || run.status === "RUNNING") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-border px-2 py-0.5 text-xs text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
        Reviewing…
      </span>
    )
  }
  if (run.status === "FAILED") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-destructive/40 px-2 py-0.5 text-xs font-medium text-destructive">
        <X className="h-3 w-3" aria-hidden="true" />
        Review failed
      </span>
    )
  }
  const pending = run.findings.filter((f) => f.status !== "DISMISSED")
  const major = pending.filter((f) => f.severity === "MAJOR").length
  const minor = pending.filter((f) => f.severity === "MINOR").length
  const info = pending.filter((f) => f.severity === "INFO").length
  if (pending.length === 0) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-border px-2 py-0.5 text-xs font-medium text-foreground">
        <ShieldCheck className="h-3 w-3" aria-hidden="true" />
        {run.framework.name}: clean
      </span>
    )
  }
  const parts = [
    major > 0 ? `${major} major` : null,
    minor > 0 ? `${minor} minor` : null,
    info > 0 ? `${info} info` : null,
  ].filter(Boolean)
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium",
        major > 0 ? "border-destructive/40 text-destructive" : "border-border text-foreground"
      )}
    >
      {major > 0 ? (
        <AlertTriangle className="h-3 w-3" aria-hidden="true" />
      ) : (
        <AlertCircle className="h-3 w-3" aria-hidden="true" />
      )}
      {run.framework.name}: {parts.join(", ")}
    </span>
  )
}

export function ReviewRunDetail({
  run,
  onFindingStatus,
}: {
  run: ReviewRun
  onFindingStatus: (findingId: string, status: "PENDING" | "ACCEPTED" | "DISMISSED") => void
}) {
  if (run.status === "FAILED") {
    return (
      <div className="text-sm">
        <p className="font-medium text-foreground">{run.framework.name}</p>
        <p role="alert" className="mt-1 text-xs text-destructive">
          {run.errorMessage || "The review failed."}
        </p>
      </div>
    )
  }
  if (run.status !== "SUCCEEDED") {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
        {run.framework.name}: review in progress…
      </div>
    )
  }
  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm font-medium text-foreground">{run.framework.name}</p>
      {run.summary && <p className="text-sm text-muted-foreground">{run.summary}</p>}
      {run.findings.length === 0 ? (
        <p className="text-sm text-muted-foreground">No issues raised.</p>
      ) : (
        <ul className="flex list-none flex-col gap-2">
          {run.findings.map((finding) => (
            <FindingRow key={finding.id} finding={finding} onStatus={onFindingStatus} />
          ))}
        </ul>
      )}
    </div>
  )
}

function FindingRow({
  finding,
  onStatus,
}: {
  finding: ReviewFinding
  onStatus: (findingId: string, status: FindingStatus) => void
}) {
  const meta = SEVERITY_META[finding.severity]
  const Icon = meta.icon
  const decided = finding.status !== "PENDING"
  const [showFields, setShowFields] = useState(false)
  // Something to look up: structured refs, or ids in the ground-truth text.
  const hasFields =
    (Array.isArray(finding.fieldRefs) && finding.fieldRefs.length > 0) ||
    /[0-9a-f]{8}-[0-9a-f]{4}-/i.test(finding.evidenceQuote ?? "")
  return (
    <li
      className={cn(
        "rounded-lg border border-border/70 bg-card p-3",
        finding.status === "DISMISSED" && "opacity-60"
      )}
    >
      <div className="flex items-start gap-2">
        <Icon className={cn("mt-0.5 h-4 w-4 shrink-0", meta.className)} aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-foreground">
            <span className={cn("mr-2 text-xs font-semibold uppercase tracking-wide", meta.className)}>
              {meta.label}
            </span>
            {finding.title}
          </p>
          {finding.item && (
            <p className="text-xs text-muted-foreground">Check: {finding.item.title}</p>
          )}
          <p className="mt-1 text-sm text-muted-foreground">{finding.detail}</p>
          {finding.sourceQuote && (
            <blockquote className="mt-2 border-l-2 border-border pl-2 text-xs italic text-muted-foreground">
              &ldquo;{finding.sourceQuote}&rdquo;
            </blockquote>
          )}
          {finding.evidenceQuote && (
            <p className="mt-1 text-xs text-muted-foreground">
              <span className="font-medium">Ground truth:</span> {finding.evidenceQuote}
            </p>
          )}
          {hasFields && (
            <button
              type="button"
              onClick={() => setShowFields((v) => !v)}
              aria-expanded={showFields}
              className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-foreground underline underline-offset-2"
            >
              {showFields ? (
                <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
              )}
              {showFields ? "Hide the fields" : "Show and fix the fields"}
            </button>
          )}
          {showFields && (
            <FindingFields
              findingId={finding.id}
              // Fixing the data is acting on the finding.
              onSaved={() => finding.status === "PENDING" && onStatus(finding.id, "ACCEPTED")}
            />
          )}
          {decided && (
            <p className="mt-2 text-xs text-muted-foreground">
              {finding.status === "ACCEPTED" ? "Accepted" : "Dismissed"}
              {finding.reviewedBy?.name ? ` by ${finding.reviewedBy.name}` : ""}
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {decided ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onStatus(finding.id, "PENDING")}
              aria-label={`Undo decision on finding: ${finding.title}`}
            >
              <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
            </Button>
          ) : (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onStatus(finding.id, "ACCEPTED")}
                aria-label={`Accept finding: ${finding.title}`}
              >
                <Check className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
                Accept
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onStatus(finding.id, "DISMISSED")}
                aria-label={`Dismiss finding: ${finding.title}`}
              >
                <X className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
                Dismiss
              </Button>
            </>
          )}
        </div>
      </div>
    </li>
  )
}
